// src/views/components/LoginPopup/LoginPopup.jsx
import React, { useEffect, useState } from 'react';
import { X, ArrowRight, User, Mail, Lock, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../../controllers/context/AuthContext';
import { sanitizePhoneInput } from '../../../controllers/utils/phoneInput';
import './LoginPopup.css';

const LoginPopup = () => {
  const {
    isLoginPopupOpen,
    closeLoginPopup,
    login,
    signup,
    verifySignupOtp,
    resendSignupOtp,
    sendPasswordResetEmail,
    updatePassword,
    isPasswordRecovery,
  } = useAuth();
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [isOtpMode, setIsOtpMode] = useState(false);
  const [otpType, setOtpType] = useState('signup'); // 'signup' | 'email' | 'recovery'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const OTP_WAIT_SECONDS = 60;

  const formatOtpTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const startOtpTimer = () => {
    setOtpSecondsLeft(OTP_WAIT_SECONDS);
  };

  useEffect(() => {
    if (!isLoginPopupOpen || !isOtpMode || otpSecondsLeft <= 0) return undefined;
    const timeoutId = setTimeout(() => {
      setOtpSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [isLoginPopupOpen, isOtpMode, otpSecondsLeft]);

  useEffect(() => {
    if (isPasswordRecovery && isLoginPopupOpen) {
      setIsResetMode(true);
      setIsForgotMode(false);
      setIsSignUpMode(false);
      setIsOtpMode(false);
      setErrorMsg('');
      setSuccessMsg('Create a new password for your account.');
      setPassword('');
      setConfirmPassword('');
    }
  }, [isPasswordRecovery, isLoginPopupOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    setIsLoading(true);

    try {
      if (isOtpMode) {
        if (!otpCode || otpCode.trim().length !== 6) {
          setErrorMsg('Please enter the 6-digit OTP from your email.');
          setIsLoading(false);
          return;
        }
        const res = await verifySignupOtp(email, otpCode, otpType);
        if (!res.success) {
          setErrorMsg(res.error || 'Invalid or expired OTP.');
        } else if (res.needsPasswordReset || otpType === 'recovery') {
          setIsOtpMode(false);
          setIsForgotMode(false);
          setIsResetMode(true);
          setOtpCode('');
          setPassword('');
          setConfirmPassword('');
          setSuccessMsg('OTP verified. Create a new password for your account.');
        } else {
          setSuccessMsg('Email verified successfully!');
          setTimeout(() => resetAndClose(), 800);
        }
      } else if (isResetMode) {
        if (!password || password.length < 6) {
          setErrorMsg('Password must be at least 6 characters.');
          setIsLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          setErrorMsg('Passwords do not match.');
          setIsLoading(false);
          return;
        }
        const res = await updatePassword(password);
        if (!res.success) {
          setErrorMsg(res.error || 'Could not update password.');
        } else {
          setSuccessMsg('Password updated successfully. You are now logged in.');
          setTimeout(() => resetAndClose(), 1200);
        }
      } else if (isForgotMode) {
        if (!email) {
          setErrorMsg('Please enter your email address.');
          setIsLoading(false);
          return;
        }
        const res = await sendPasswordResetEmail(email);
        if (!res.success) {
          setErrorMsg(res.error || 'Could not send reset OTP.');
        } else {
          setIsOtpMode(true);
          setIsForgotMode(false);
          setOtpType('recovery');
          setOtpCode('');
          startOtpTimer();
          setSuccessMsg(`OTP sent to ${email}. Enter the code to reset your password.`);
        }
      } else if (isSignUpMode) {
        if (!fullName || !phoneNumber || !email || !password) {
          setErrorMsg('Please fill all fields');
          setIsLoading(false);
          return;
        }
        const res = await signup(email, password, fullName, phoneNumber);
        if (!res?.success) {
          const msg =
            typeof res?.error === 'string' && res.error.trim()
              ? res.error
              : 'Registration failed. Please try again.';
          setErrorMsg(msg);
        } else if (res.needsEmailVerification) {
          setIsOtpMode(true);
          setIsSignUpMode(false);
          setOtpType(res.otpType || 'signup');
          setErrorMsg('');
          setOtpCode('');
          startOtpTimer();
          setSuccessMsg(`OTP sent to ${res.email || email}. Enter the code to verify.`);
        } else {
          // Never skip OTP after signup
          setIsOtpMode(true);
          setIsSignUpMode(false);
          setOtpType('email');
          setErrorMsg('');
          setOtpCode('');
          startOtpTimer();
          setSuccessMsg(`OTP sent to ${email}. Enter the code to verify.`);
        }
      } else {
        if (!email || !password) {
          setErrorMsg('Please fill in email and password');
          setIsLoading(false);
          return;
        }
        const res = await login(email, password);
        if (!res.success) {
          if (res.needsEmailVerification) {
            setIsOtpMode(true);
            setIsSignUpMode(false);
            setOtpType(res.otpType || 'email');
            setOtpCode('');
            startOtpTimer();
            setSuccessMsg(res.error || `OTP sent to ${email}.`);
          } else {
            setErrorMsg(res.error || 'Invalid credentials.');
          }
        } else {
          resetAndClose();
        }
      }
    } catch (err) {
      setErrorMsg(err.message || 'An error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetAndClose = () => {
    closeLoginPopup();
    setTimeout(() => {
      setIsSignUpMode(false);
      setIsForgotMode(false);
      setIsResetMode(false);
      setIsOtpMode(false);
      setOtpType('signup');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setOtpCode('');
      setOtpSecondsLeft(0);
      setFullName('');
      setPhoneNumber('');
      setErrorMsg('');
      setSuccessMsg('');
      setIsLoading(false);
    }, 300);
  };

  const handleResendOtp = async () => {
    if (otpSecondsLeft > 0) return;
    setErrorMsg('');
    setSuccessMsg('');
    setIsLoading(true);
    try {
      const res = await resendSignupOtp(email, otpType);
      if (!res.success) {
        setErrorMsg(res.error || 'Could not resend OTP.');
      } else {
        startOtpTimer();
        setSuccessMsg(`A new 6-digit OTP was sent to ${email}.`);
      }
    } catch (err) {
      setErrorMsg(err.message || 'Could not resend OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const headerTitle = isOtpMode
    ? 'Verify Email'
    : isResetMode
      ? 'Set New Password'
      : isForgotMode
        ? 'Forgot Password'
        : isSignUpMode
          ? 'Create Account'
          : 'Welcome Back';

  const headerSubtitle = isOtpMode
    ? `Enter the 6-digit OTP sent to ${email}.`
    : isResetMode
      ? 'Enter a new password to secure your account.'
      : isForgotMode
        ? 'Enter your registered email and we will send a 6-digit OTP.'
        : isSignUpMode
          ? 'Join NoBrokerage to find your dream property or list your home.'
          : 'Login to save properties, watch reels, and chat in real-time.';

  return (
    <AnimatePresence>
      {isLoginPopupOpen && (
        <motion.div
          className="popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={resetAndClose}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <motion.div
            className="login-popup-content"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.88, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.88, y: 30, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          >
            <motion.button
              className="login-popup-close"
              onClick={resetAndClose}
              aria-label="Close Login"
              whileHover={{ rotate: 90, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </motion.button>

            <div className="login-header">
              <h2>{headerTitle}</h2>
              <p>{headerSubtitle}</p>
            </div>

            {errorMsg && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                style={{
                  color: '#ef4444',
                  background: 'rgba(239, 68, 68, 0.15)',
                  padding: '0.75rem',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  textAlign: 'center',
                }}
              >
                {errorMsg}
              </motion.div>
            )}

            {successMsg && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                style={{
                  color: '#15803d',
                  background: 'rgba(34, 197, 94, 0.15)',
                  padding: '0.75rem',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  textAlign: 'center',
                }}
              >
                {successMsg}
              </motion.div>
            )}

            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {isSignUpMode && !isForgotMode && !isResetMode && !isOtpMode && (
                  <motion.div
                    key="signup-fields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Full Name */}
                    <div className="login-form-group">
                      <label>Full Name</label>
                      <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <User size={18} style={{ position: 'absolute', left: '1.25rem', color: 'var(--text-muted)' }} />
                        <input
                          type="text"
                          className="login-input"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder="e.g. Rahul Sharma"
                          style={{ paddingLeft: '3.5rem' }}
                          required
                        />
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div className="login-form-group">
                      <label>Phone Number</label>
                      <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <Phone size={18} style={{ position: 'absolute', left: '1.25rem', color: 'var(--text-muted)' }} />
                        <input
                          type="tel"
                          inputMode="numeric"
                          className="login-input"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(sanitizePhoneInput(e.target.value))}
                          placeholder="e.g. 9214901596"
                          style={{ paddingLeft: '3.5rem' }}
                          maxLength={10}
                          required
                        />
                      </div>
                    </div>

                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email - login / signup / forgot */}
              {!isResetMode && !isOtpMode && (
                <div className="login-form-group">
                  <label>Email Address</label>
                  <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Mail size={18} style={{ position: 'absolute', left: '1.25rem', color: 'var(--text-muted)' }} />
                    <input
                      type="email"
                      className="login-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email address"
                      style={{ paddingLeft: '3.5rem' }}
                      required
                    />
                  </div>
                </div>
              )}

              {/* OTP verification */}
              {isOtpMode && (
                <div className="login-form-group">
                  <label>Email OTP</label>
                  <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Lock size={18} style={{ position: 'absolute', left: '1.25rem', color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      inputMode="numeric"
                      className="login-input"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      style={{ paddingLeft: '3.5rem', letterSpacing: '0.25em', fontWeight: 700 }}
                      required
                      maxLength={6}
                    />
                  </div>
                  {otpSecondsLeft > 0 ? (
                    <p className="otp-timer-text">OTP expires in {formatOtpTime(otpSecondsLeft)}</p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                      className="otp-resend-btn"
                    >
                      {isLoading ? 'Sending...' : 'Resend OTP'}
                    </button>
                  )}
                </div>
              )}

              {/* Password - login / signup / reset */}
              {!isForgotMode && !isOtpMode && (
                <div className="login-form-group">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ marginBottom: 0 }}>{isResetMode ? 'New Password' : 'Password'}</label>
                    {!isSignUpMode && !isResetMode && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotMode(true);
                          setErrorMsg('');
                          setSuccessMsg('');
                          setPassword('');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--accent)',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Lock size={18} style={{ position: 'absolute', left: '1.25rem', color: 'var(--text-muted)' }} />
                    <input
                      type="password"
                      className="login-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={isResetMode ? 'Enter new password' : 'Enter password'}
                      style={{ paddingLeft: '3.5rem' }}
                      required
                      minLength="6"
                    />
                  </div>
                </div>
              )}

              {/* Confirm password - reset only */}
              {isResetMode && (
                <div className="login-form-group">
                  <label>Confirm New Password</label>
                  <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <Lock size={18} style={{ position: 'absolute', left: '1.25rem', color: 'var(--text-muted)' }} />
                    <input
                      type="password"
                      className="login-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      style={{ paddingLeft: '3.5rem' }}
                      required
                      minLength="6"
                    />
                  </div>
                </div>
              )}

              <motion.button
                type="submit"
                className="login-btn login-btn-accent"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '0.5rem' }}
              >
                {isLoading ? (
                  'Processing...'
                ) : isOtpMode ? (
                  <>Verify & Continue <ArrowRight size={18} /></>
                ) : isResetMode ? (
                  <>Update Password <ArrowRight size={18} /></>
                ) : isForgotMode ? (
                  <>Send OTP <ArrowRight size={18} /></>
                ) : isSignUpMode ? (
                  <>Register & Sign Up <ArrowRight size={18} /></>
                ) : (
                  <>Login Securely <ArrowRight size={18} /></>
                )}
              </motion.button>

              <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
                <button
                  type="button"
                  className="login-change-number"
                  onClick={() => {
                    if (isForgotMode || isResetMode || isOtpMode) {
                      setIsForgotMode(false);
                      setIsResetMode(false);
                      setIsOtpMode(false);
                      setIsSignUpMode(false);
                      setOtpCode('');
                      setOtpSecondsLeft(0);
                    } else {
                      setIsSignUpMode(!isSignUpMode);
                    }
                    setErrorMsg('');
                    setSuccessMsg('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  style={{
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    color: 'var(--accent)',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                  }}
                >
                  {isForgotMode || isResetMode || isOtpMode
                    ? 'Back to Login'
                    : isSignUpMode
                      ? 'Already have an account? Log In'
                      : "Don't have an account? Sign Up"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoginPopup;
